import echarts from 'echarts';
import Color from 'color';

const COLORS = {
	BRAND: '#5637C8',
	OPENNESS: {
		PRIMARY: '#4A1549',
		LIGHT: '#faecf9',
	},
	CONSCIENTIOUSNESS: {
		PRIMARY: '#E01E5A',
		LIGHT: '#fdeff3',
	},
	EXTRAVERSION: {
		PRIMARY: '#2DB67B',
		LIGHT: '#e8f9f2',
	},
	AGREEABLENESS: {
		PRIMARY: '#36C5EF',
		LIGHT: '#e3f7fd',
	},
	NEUROTICISM: {
		PRIMARY: '#ECB32E',
		LIGHT: '#fdf7e9',
	},
	WHITE: '#FFFFFF',
	LOW_RED: '#FF6663',
	HIGH_GREEN: '#6EB257',
};

const BACKGROUND_COLOR = '#F4F6F8';

const LABEL_FONT_SIZE = 12;

export default {
	COLORS,
	global: {
		title: {
			left: 'center',
			top: '32px',
			textStyle: {
				fontFamily: "'Open Sans',sans-serif",
				fontSize: '20',
			},
		},
		label: {
			show: true,
			fontSize: 12,
			fontFamily: 'AvenirLTW01-35Light',
		},
		backgroundColor: BACKGROUND_COLOR,
		grid: {
			containLabel: true,
		},
	},
	Lift: {
		grid: {
			containLabel: false,
		},
		xAxis: {
			nameLocation: 'center',
			nameGap: 30,
			axisTick: {
				show: false,
			},
			axisPointer: {
				label: {
					formatter: (params) => `${params.value - 10} - ${params.value} percentile of users`,
				},
			},
		},
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'cross',
			},
			backgroundColor: 'rgba(225, 225, 225, 0.7)',
			textStyle: {
				color: 'rgba(25, 25, 25, 1)',
			},
		},
		Line: {
			yAxis: {
				nameLocation: 'middle',
				nameGap: 45,
				nameRotate: 90,
				splitLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
				axisLabel: {
					formatter: (value) => `${value.toFixed(1)}x`,
				},
				axisPointer: {
					label: {
						formatter: (params) => `${params.value.toFixed(1)}x`,
					},
				},
			},
			series: {
				showSymbol: false,
			},
			lineStyle: {
				width: 5,
				shadowColor: 'rgba(139, 45, 139, 0.6)',
				shadowBlur: 10,
				shadowOffsetY: 20,
			},
		},
		Bar: {
			yAxis: {
				nameLocation: 'middle',
				nameGap: 60,
				nameRotate: 90,
				splitLine: {
					show: false,
				},
				axisTick: {
					show: false,
				},
				axisLabel: {
					formatter: (value) => `${value.toFixed(1)}%`,
				},
				splitNumber: 10,
				axisPointer: {
					label: {
						formatter: (params) => `${params.value.toFixed(1)}%`,
					},
				},
			},
			series: {
				animationDelay: () => Math.random() * 200,
				itemStyle: {
					opacity: 0.7,
				},
			},
		},
	},
	Slider: {
		Scatter: {
			series: {
				symbol: 'roundRect',
				symbolSize: [15, 30],
			},
			itemStyle: {
				shadowBlur: 3,
				shadowOffsetX: 2,
				shadowOffsetY: 2,
			},
			label: {
				show: true,
				position: 'top',
			},
		},
		grid: {
			top: '30',
			bottom: '60',
			left: '20%',
			right: '20%',
		},
		yAxis: {
			axisLabel: {
				fontSize: LABEL_FONT_SIZE + 2,
			},
		},
		xAxis: {
			nameLocation: 'middle',
			nameGap: 15,
			nameTextStyle: {
				fontSize: LABEL_FONT_SIZE + 4,
			},
		},
		areaStyle: () => ({
			color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
				{
					offset: 0,
					color: COLORS.LOW_RED,
				},
				{
					offset: 0.5,
					color: COLORS.WHITE,
				},
				{
					offset: 1,
					color: COLORS.HIGH_GREEN,
				},
			]),
		}),
		lineStyle: {
			color: '#000000',
			width: 0,
			shadowBlur: 3,
		},
		series: {
			animation: true,
		},
	},
	NormalDistribution: {
		areaStyle: (factor) => ({
			color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
				{
					offset: 0,
					color: COLORS[factor.toUpperCase()].PRIMARY,
				},
				{
					offset: 0.5,
					color: COLORS[factor.toUpperCase()].LIGHT,
				},
				{
					offset: 1,
					color: COLORS[factor.toUpperCase()].PRIMARY,
				},
			]),
		}),
		lineStyle: {
			color: '#000000',
			width: 0,
			shadowBlur: 3,
		},
		markPoint: {
			label: {
				color: '#FFF',
			},
			itemStyle: {
				color: '#000',
				opacity: 0.8,
			},
			animation: false,
		},
		markLine: {
			symbol: 'none',
			label: {
				position: 'start',
			},
			lineStyle: {
				color: '#000',
				opacity: 0.4,
			},
			animation: false,
		},
		series: {
			animation: false,
		},
	},
	Gauge: {
		itemStyle: {
			color: '#000000',
			opacity: 0.5,
		},
		pointer: {
			length: '91%',
			width: '2%',
			show: true,
		},
		axisLabel: {
			show: true,
			textStyle: {
				fontSize: 8,
				color: '#000000',
				padding: [0, -10, 0, -10],
			},
		},
		title: {
			offsetCenter: ['0', '0'],
			fontSize: 12,
			color: '#000000',
		},
		axisLine: (factor) => ({
			lineStyle: {
				width: 15,
				color: [
					[
						0.5,
						new echarts.graphic.LinearGradient(0, 1, 0, 0, [
							{
								offset: 0,
								color: COLORS[factor.toUpperCase()].PRIMARY,
							},
							{
								offset: 1,
								color: COLORS[factor.toUpperCase()].LIGHT,
							},
						]),
					],
					[
						1,
						new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{
								offset: 0,
								color: COLORS[factor.toUpperCase()].LIGHT,
							},
							{
								offset: 1,
								color: COLORS[factor.toUpperCase()].PRIMARY,
							},
						]),
					],
				],
			},
		}),
		axisTick: {
			show: true,
			lineStyle: {
				width: 1,
				color: '#000000',
				opacity: 0,
			},
		},
		splitNumber: 10,
		splitLine: {
			show: false,
			length: 35,
			lineStyle: {
				width: 1,
				color: '#000000',
				opacity: 0.5,
			},
		},
		detail: {
			show: true,
			offsetCenter: ['0', '-40%'],
			fontSize: 20,
			fontWeight: 'bold',
		},
	},
	Nightingale: {
		series: {
			animationType: 'scale',
			animationEasing: 'elasticOut',
			animationDelay: () => Math.random() * 200,
			radius: ['15%', '50%'],
		},
		visualMap: (factor) => ({
			inRange: {
				color: [COLORS[factor.toUpperCase()].LIGHT, COLORS[factor.toUpperCase()].PRIMARY],
			},
		}),
		selectedItemStyle: {
			shadowBlur: 2,
			shadowOffsetX: -1,
			shadowOffsetY: -1,
			shadowColor: '#909090',
			borderColor: '#FFF',
			borderWidth: 4,
		},
		itemStyle: (factor, proportion, shade) => {
			if (shade) {
				const lightenedColor = Color(COLORS[factor.toUpperCase()].PRIMARY)
					.lighten(proportion / 2)
					.hex()
					.toString();
				return {
					color:
						lightenedColor === '#FFFFFF'
							? Color(COLORS[factor.toUpperCase()].LIGHT)
									.darken(1 - proportion / 2)
									.hex()
									.toString()
							: lightenedColor,
					borderWidth: 1,
					borderColor: '#FFFFFF',
				};
			}
			return {
				color: COLORS[factor.toUpperCase()].PRIMARY,
				borderWidth: 1,
				borderColor: '#FFFFFF',
			};
		},
		label: {
			color: '#000',
			lineHeight: 30,
			position: 'outside',
			fontSize: LABEL_FONT_SIZE,
		},
		tooltip: {
			formatter: (params) =>
				`The ${params.data.trait} score for this Customer Group is in the ${params.data.percentile.toFixed(
					1
				)}th percentile, meaning that the average customer in the group is more ${
					params.data.rangeName
				} than ${Math.max(params.data.percentile, 100 - params.data.percentile).toFixed(
					1
				)}% of the US population`.replace(/(?![^\n]{1,64}$)([^\n]{1,64})\s/g, '$1<br />'),
		},
	},
	Radar: {
		radar: {
			shape: 'polygon',
			silent: false,
			triggerEvent: true,
		},
		startAngle: (labels) => 90 + 360 / labels.length,
		axisLine: {
			show: true,
			lineStyle: {
				color: ['#333'],
			},
		},
		splitLine: {
			show: true,
			lineStyle: {
				color: ['#999'],
			},
		},
		splitArea: {
			show: true,
			areaStyle: ['#FEFEFE'],
		},
		itemStyle: {},
		lineStyle: {
			width: 4,
		},
	},
	heatmap: {
		xAxis: {
			position: 'top',
			axisLine: {
				show: false,
			},
			axisTick: {
				show: false,
			},
			splitLine: {
				show: false,
			},
			axisLabel: {
				show: true,
				interval: 0,
			},
		},
		yAxis: {
			axisLine: {
				show: false,
			},
			axisTick: {
				show: false,
			},
			splitLine: {
				show: false,
			},
			axisLabel: {
				show: true,
				interval: 0,
			},
		},
		visualMap: {
			series: {
				inRange: {
					color: ['#FF6663', 'white', '#6EB257'],
				},
			},
			strength: {
				inRange: {
					color: ['#E9E6F8', COLORS.BRAND],
				},
			},
		},
	},
};
